import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertWorkOrderSchema } from "@shared/schema";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search, AlertCircle, User, Building2, ChevronDown } from "lucide-react";
import { formatPhoneNumber } from "@/lib/utils";

interface ServiceType {
  id: string; // The id IS the slug (e.g., "mc_application")
  name: string;
  description: string | null;
  category: string;
  estimatedDays: string | null;
  isActive: boolean;
  credentialTypeIds?: number[];
}

interface CredentialType {
  id: number;
  name: string;
  description: string | null;
  agency: string | null;
  isActive: boolean;
}

const formSchema = insertWorkOrderSchema.extend({
  contactId: z.number().min(1, "Please select a requesting contact"),
  companyId: z.number().min(1, "Please select a company"),
  carrierId: z.number().optional(),
  secondaryContactId: z.number().optional(),
  serviceType: z.string().min(1, "Please select a service"),
  description: z.string().optional(),
  assignedToUserId: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface PrePopulateData {
  contactId?: number;
  contactName?: string;
  companyId?: number;
  carrierId?: number;
}

interface CreateWorkOrderModalProps {
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  users: any[];
  prePopulate?: PrePopulateData;
  currentUserId?: string; // ID of the current user to default assignment
}

export default function CreateWorkOrderModal({
  onClose,
  onSubmit,
  isLoading,
  users,
  prePopulate,
  currentUserId
}: CreateWorkOrderModalProps) {
  const [contactSearch, setContactSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [availableCarriers, setAvailableCarriers] = useState<any[]>([]);
  const [availableSecondaryContacts, setAvailableSecondaryContacts] = useState<any[]>([]);
  const [contactError, setContactError] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [requestingContactServiceBureauId, setRequestingContactServiceBureauId] = useState<number | null>(null);
  const [showContactChangeDropdown, setShowContactChangeDropdown] = useState(false);
  const [selectedContactServiceBureauName, setSelectedContactServiceBureauName] = useState<string | null>(null);
  const [selectedCredentialTypeIds, setSelectedCredentialTypeIds] = useState<number[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contactId: prePopulate?.contactId || 0,
      companyId: prePopulate?.companyId || 0,
      carrierId: prePopulate?.carrierId,
      secondaryContactId: undefined,
      serviceType: "",
      description: "",
      assignedToUserId: currentUserId || "unassigned",
    },
  });

  const selectedContactId = watch("contactId");
  const selectedCompanyId = watch("companyId");
  const selectedCarrierId = watch("carrierId");
  const selectedSecondaryContactId = watch("secondaryContactId");
  const selectedServiceType = watch("serviceType");

  // Fetch contacts for fuzzy search
  const { data: contacts = [] } = useQuery<any[]>({
    queryKey: ["/api/contacts"],
    enabled: contactSearch.length >= 1 || !!prePopulate?.contactId,
  });

  // Fetch contact-company relationships for selected contact
  const { data: contactCompanies = [], isLoading: isLoadingContactCompanies } = useQuery<any[]>({
    queryKey: [`/api/contacts/${selectedContactId}/companies`],
    enabled: selectedContactId > 0,
  });

  // Fetch carriers for selected company
  const { data: companyCarriers = [] } = useQuery<any[]>({
    queryKey: [`/api/companies/${selectedCompanyId}/carriers`],
    enabled: selectedCompanyId > 0,
  });

  // Fetch secondary contact options (company contacts + requesting contact's service bureau contacts)
  const { data: secondaryContactOptions = [] } = useQuery({
    queryKey: [`/api/secondary-contacts/${selectedCompanyId}`, requestingContactServiceBureauId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (requestingContactServiceBureauId) {
        params.set('serviceBureauId', requestingContactServiceBureauId.toString());
      }
      const url = `/api/secondary-contacts/${selectedCompanyId}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: selectedCompanyId > 0,
  });

  // Fetch all companies (needed for service bureau contacts)
  const { data: allCompanies = [] } = useQuery<any[]>({
    queryKey: ["/api/companies"],
    enabled: true,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  // Fetch service types from database
  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ["/api/service-types"],
  });

  // Fetch credential types from database
  const { data: credentialTypes = [] } = useQuery<CredentialType[]>({
    queryKey: ["/api/credential-types"],
  });

  // Filter to only active credential types
  const activeCredentialTypes = useMemo(() => {
    return credentialTypes.filter(ct => ct.isActive).sort((a, b) => a.name.localeCompare(b.name));
  }, [credentialTypes]);

  // Filter to only active service types, then filter by selected credential types
  const activeServiceTypes = useMemo(() => {
    const active = serviceTypes.filter(st => st.isActive);
    
    // If no credential types selected, show all active service types
    if (selectedCredentialTypeIds.length === 0) {
      return active.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    // Filter to service types that have ALL selected credential types
    const filtered = active.filter(st => {
      if (!st.credentialTypeIds || st.credentialTypeIds.length === 0) return false;
      return selectedCredentialTypeIds.every(ctId => st.credentialTypeIds!.includes(ctId));
    });
    
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [serviceTypes, selectedCredentialTypeIds]);

  // Fetch other contacts from the same company (for contact switching)
  const { data: sameCompanyContacts = [] } = useQuery<any[]>({
    queryKey: [`/api/companies/${requestingContactServiceBureauId}/contacts`],
    enabled: requestingContactServiceBureauId !== null && requestingContactServiceBureauId > 0,
  });

  // Fetch resolved service rate for selected company and service type
  const { data: resolvedRate, isFetching: isRateFetching } = useQuery<{ rate: string; isCustom: boolean; rateId: number } | null>({
    queryKey: ['/api/resolved-service-rate', selectedCompanyId, selectedServiceType],
    enabled: selectedCompanyId > 0 && !!selectedServiceType,
    queryFn: async () => {
      const response = await fetch(`/api/resolved-service-rate/${selectedCompanyId}/${selectedServiceType}`);
      if (!response.ok) return null;
      return response.json();
    },
  });

  // True when a company + service type are selected, rate query is done, and no rate was found
  const noRateInEffect = selectedCompanyId > 0 && !!selectedServiceType && !isRateFetching && resolvedRate === null;

  // Filter contacts based on search
  const filteredContacts = contacts.filter((contact: any) =>
    contact.firstName?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    contact.lastName?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    contact.email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    contact.phone?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  // Filter companies based on search
  const filteredCompanies = useMemo(() => {
    if (!companySearch) return availableCompanies;
    const searchLower = companySearch.toLowerCase();
    return availableCompanies.filter((company: any) =>
      company.name?.toLowerCase().includes(searchLower) ||
      company.dotProfile?.dotNumber?.toLowerCase().includes(searchLower)
    );
  }, [availableCompanies, companySearch]);

  // Get selected company name for display
  const selectedCompanyDisplay = useMemo(() => {
    const company = availableCompanies.find((c: any) => c.id === selectedCompanyId);
    if (!company) return "";
    return company.dotProfile?.dotNumber ? `${company.name} - ${company.dotProfile.dotNumber}` : company.name;
  }, [availableCompanies, selectedCompanyId]);

  // Handle pre-population
  useEffect(() => {
    if (prePopulate?.contactId && contacts.length > 0) {
      const contact = contacts.find((c: any) => c.id === prePopulate.contactId);
      if (contact) {
        setSelectedContact(contact);
        setContactSearch(`${contact.firstName} ${contact.lastName}`);
        setValue("contactId", contact.id);
      }
    }
    if (prePopulate?.contactName && !prePopulate?.contactId) {
      setContactSearch(prePopulate.contactName);
    }
  }, [prePopulate, contacts, setValue]);

  // Handle contact selection
  const handleContactSelect = (contact: any) => {
    setSelectedContact(contact);
    setValue("contactId", contact.id);
    setContactSearch(`${contact.firstName} ${contact.lastName}`);
    setContactError("");
    setShowContactChangeDropdown(false);
    
    // Reset company and carrier when contact changes
    setValue("companyId", 0);
    setValue("carrierId", undefined);
    setValue("secondaryContactId", undefined);
    setCompanySearch("");
    setAvailableCompanies([]);
    setAvailableCarriers([]);
    setAvailableSecondaryContacts([]);
    setRequestingContactServiceBureauId(null);
    setSelectedContactServiceBureauName(null);
  };
  
  // Handle switching to a different contact from the same company
  const handleContactSwitch = (contact: any) => {
    setSelectedContact(contact);
    setValue("contactId", contact.id);
    setContactSearch(`${contact.firstName} ${contact.lastName}`);
    setShowContactChangeDropdown(false);
    // Keep the same company selection and service bureau info
  };

  // Handle company selection
  const handleCompanySelect = (company: any) => {
    setValue("companyId", company.id);
    setCompanySearch(company.dotProfile?.dotNumber ? `${company.name} - ${company.dotProfile.dotNumber}` : company.name);
    setShowCompanyDropdown(false);
    setValue("carrierId", undefined);
    setValue("secondaryContactId", undefined);
  };

  // Update available companies when contact companies are loaded
  useEffect(() => {
    if (isLoadingContactCompanies || selectedContactId === 0) {
      return;
    }
    
    setContactError("");
    
    if (contactCompanies.length === 0) {
      setContactError("This contact has no company relationships. Please create a company relationship first.");
      setAvailableCompanies([]);
      return;
    }
    
    if (contactCompanies.length > 0) {
      // Check if this contact is associated with any service bureau companies
      const serviceBureauRelationship = contactCompanies.find((cc: any) => cc.company.type === 'service_bureau');
      
      if (serviceBureauRelationship) {
        // Store the service bureau ID and name for filtering secondary contacts and display
        setRequestingContactServiceBureauId(serviceBureauRelationship.company.id);
        setSelectedContactServiceBureauName(serviceBureauRelationship.company.name);
        
        // Service bureau contacts can create work orders for ANY company
        if (allCompanies.length > 0) {
          const sortedAllCompanies = [...allCompanies]
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
          setAvailableCompanies(sortedAllCompanies);
        }
      } else {
        setRequestingContactServiceBureauId(null);
        setSelectedContactServiceBureauName(null);
        // Regular contacts can only create work orders for their associated companies
        const sortedCompanies = contactCompanies
          .map((cc: any) => ({ ...cc.company, relationshipType: cc.relationshipType, isPrimary: cc.isPrimary }))
          .sort((a: any, b: any) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return a.name.localeCompare(b.name);
          });
        
        setAvailableCompanies(sortedCompanies);
        
        // Auto-select if only one company
        if (sortedCompanies.length === 1) {
          setValue("companyId", sortedCompanies[0].id);
          setCompanySearch(sortedCompanies[0].dotProfile?.dotNumber 
            ? `${sortedCompanies[0].name} - ${sortedCompanies[0].dotProfile.dotNumber}` 
            : sortedCompanies[0].name);
        }
      }
    }
  }, [contactCompanies, selectedContactId, setValue, isLoadingContactCompanies, allCompanies]);

  // Handle pre-populated company and carrier after companies are loaded
  useEffect(() => {
    if (prePopulate?.companyId && availableCompanies.length > 0 && selectedCompanyId === 0) {
      const company = availableCompanies.find((c: any) => c.id === prePopulate.companyId);
      if (company) {
        setValue("companyId", company.id);
        setCompanySearch(company.dotProfile?.dotNumber ? `${company.name} - ${company.dotProfile.dotNumber}` : company.name);
      }
    }
  }, [prePopulate, availableCompanies, selectedCompanyId, setValue]);

  // Handle pre-populated carrier after carriers are loaded
  useEffect(() => {
    if (prePopulate?.carrierId && availableCarriers.length > 0 && !selectedCarrierId) {
      const carrier = availableCarriers.find((c: any) => c.id === prePopulate.carrierId);
      if (carrier) {
        setValue("carrierId", carrier.id);
      }
    }
  }, [prePopulate, availableCarriers, selectedCarrierId, setValue]);

  // Sync companyCarriers from query to local state
  useEffect(() => {
    setAvailableCarriers(companyCarriers);
  }, [companyCarriers]);

  // Auto-select carrier when there's only one available (99% use case)
  // This runs after availableCarriers state is updated
  useEffect(() => {
    // Only auto-select if exactly one carrier and no carrier currently selected
    if (availableCarriers.length === 1 && !selectedCarrierId) {
      setValue("carrierId", availableCarriers[0].id);
    }
  }, [availableCarriers, selectedCarrierId, setValue]);

  // Update available secondary contacts when options are loaded
  useEffect(() => {
    if (secondaryContactOptions.length > 0) {
      // Exclude the primary contact from secondary options
      const filteredOptions = secondaryContactOptions.filter((contact: any) => contact.id !== selectedContactId);
      setAvailableSecondaryContacts(filteredOptions);
    } else {
      setAvailableSecondaryContacts([]);
      setValue("secondaryContactId", undefined);
    }
  }, [secondaryContactOptions, selectedContactId, setValue]);

  const handleFormSubmit = (data: FormData) => {
    if (contactError) {
      return;
    }
    
    const formattedData = {
      ...data,
      assignedToUserId: data.assignedToUserId === "unassigned" ? null : data.assignedToUserId,
      carrierId: data.carrierId || null,
      secondaryContactId: data.secondaryContactId || null,
    };
    onSubmit(formattedData as any);
  };

  
  // Format carrier display name
  const formatCarrierDisplay = (carrier: any) => {
    const prefix = carrier.docketPrefix || "";
    const docket = carrier.docketNumber || "";
    if (prefix && docket) {
      return `${carrier.name} - ${prefix}-${docket}`;
    }
    return carrier.name;
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Work Order</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            {/* Contact Search */}
            <div className="space-y-2">
              <Label htmlFor="contact">Requesting Contact *</Label>
              
              {/* Show selected contact info card when contact is selected */}
              {selectedContact ? (
                <div className="relative">
                  <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900" data-testid="text-selected-contact-name">
                            {selectedContact.firstName} {selectedContact.lastName}
                          </div>
                          {selectedContact.email && (
                            <div className="text-sm text-gray-500" data-testid="text-selected-contact-email">
                              {selectedContact.email}
                            </div>
                          )}
                          {/* Show service bureau company name if contact is from a service bureau */}
                          {selectedContactServiceBureauName && (
                            <div className="flex items-center gap-1 mt-1">
                              <Building2 className="h-3 w-3 text-blue-600" />
                              <span className="text-xs text-blue-600 font-medium" data-testid="text-contact-service-bureau">
                                {selectedContactServiceBureauName}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Change contact dropdown - only for service bureau contacts */}
                      {requestingContactServiceBureauId && sameCompanyContacts.length > 1 && (
                        <div className="relative">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowContactChangeDropdown(!showContactChangeDropdown)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                            data-testid="button-change-contact"
                          >
                            Change <ChevronDown className="h-3 w-3 ml-1" />
                          </Button>
                          
                          {showContactChangeDropdown && (
                            <div className="absolute right-0 top-full mt-1 w-64 max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg z-50">
                              {sameCompanyContacts
                                .filter((cc: any) => cc.contact?.id !== selectedContact.id)
                                .map((cc: any) => (
                                  <button
                                    key={cc.contact?.id || cc.id}
                                    type="button"
                                    onClick={() => handleContactSwitch(cc.contact)}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                                    data-testid={`switch-contact-option-${cc.contact?.id || cc.id}`}
                                  >
                                    <div className="font-medium text-sm">{cc.contact?.firstName} {cc.contact?.lastName}</div>
                                    {cc.contact?.email && <div className="text-xs text-gray-500">{cc.contact.email}</div>}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Clear selection button */}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedContact(null);
                        setValue("contactId", 0);
                        setContactSearch("");
                        setAvailableCompanies([]);
                        setValue("companyId", 0);
                        setValue("carrierId", undefined);
                        setValue("secondaryContactId", undefined);
                        setRequestingContactServiceBureauId(null);
                        setSelectedContactServiceBureauName(null);
                      }}
                      className="text-xs text-gray-400 hover:text-gray-600 mt-2"
                      data-testid="button-clear-contact"
                    >
                      Search for a different contact
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, email, or phone..."
                      value={contactSearch}
                      onChange={(e) => {
                        setContactSearch(e.target.value);
                        if (selectedContact) {
                          setSelectedContact(null);
                          setValue("contactId", 0);
                        }
                      }}
                      className="pl-8"
                      data-testid="input-contact-search"
                    />
                  </div>
                  
                  {/* Contact Search Results */}
                  {contactSearch.length >= 1 && (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white">
                      {filteredContacts.length > 0 ? (
                        filteredContacts.map((contact: any) => (
                          <button
                            key={contact.id}
                            type="button"
                            onClick={() => handleContactSelect(contact)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                            data-testid={`contact-option-${contact.id}`}
                          >
                            <div className="font-medium">{contact.firstName} {contact.lastName}</div>
                            {contact.email && <div className="text-sm text-gray-500">{contact.email}</div>}
                            {contact.phone && <div className="text-sm text-gray-500">{formatPhoneNumber(contact.phone)}</div>}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">No contacts found</div>
                      )}
                    </div>
                  )}
                </>
              )}
              
              {contactError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{contactError}</AlertDescription>
                </Alert>
              )}
              
              {errors.contactId && (
                <p className="text-sm text-red-600">{errors.contactId.message}</p>
              )}
            </div>

            {/* Company Search/Selection */}
            {availableCompanies.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="company">Company *</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search companies..."
                    value={selectedCompanyId > 0 ? selectedCompanyDisplay : companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setShowCompanyDropdown(true);
                      if (selectedCompanyId > 0) {
                        setValue("companyId", 0);
                        setValue("carrierId", undefined);
                        setValue("secondaryContactId", undefined);
                      }
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    className="pl-8"
                    data-testid="input-company-search"
                  />
                </div>
                
                {/* Company Search Results */}
                {showCompanyDropdown && selectedCompanyId === 0 && (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white">
                    {filteredCompanies.length > 0 ? (
                      filteredCompanies.slice(0, 50).map((company: any) => {
                        const isBlocked = company.status === 'blocked';
                        return (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => !isBlocked && handleCompanySelect(company)}
                            disabled={isBlocked}
                            className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 ${isBlocked ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-100'}`}
                            data-testid={`company-option-${company.id}`}
                          >
                            <div className="font-medium flex items-center gap-2">
                              {company.name}
                              {company.dotProfile?.dotNumber && (
                                <span className="text-muted-foreground"> - {company.dotProfile.dotNumber}</span>
                              )}
                              {isBlocked && (
                                <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Blocked</span>
                              )}
                            </div>
                            {company.isPrimary && !isBlocked && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">Primary</span>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No companies found</div>
                    )}
                    {filteredCompanies.length > 50 && (
                      <div className="px-3 py-2 text-sm text-gray-400 text-center">
                        Type to filter more companies...
                      </div>
                    )}
                  </div>
                )}
                
                {errors.companyId && (
                  <p className="text-sm text-red-600">{errors.companyId.message}</p>
                )}
              </div>
            )}

            {/* Carrier Selection */}
            {selectedCompanyId > 0 && (
              <div className="space-y-2">
                <Label htmlFor="carrier">Carrier (Optional)</Label>
                <Select
                  value={selectedCarrierId ? selectedCarrierId.toString() : ""}
                  onValueChange={(value) => setValue("carrierId", value ? parseInt(value) : undefined)}
                >
                  <SelectTrigger data-testid="select-carrier">
                    <SelectValue placeholder={availableCarriers.length === 0 ? "No carriers available" : "Select a carrier..."} />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {availableCarriers.length > 0 ? (
                      availableCarriers.map((carrier: any) => (
                        <SelectItem key={carrier.id} value={carrier.id.toString()}>
                          {formatCarrierDisplay(carrier)}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-carriers" disabled>No carriers for this company</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {errors.carrierId && (
                  <p className="text-sm text-red-600">{errors.carrierId.message}</p>
                )}
              </div>
            )}

            {/* Secondary Contact Selection */}
            {selectedCompanyId > 0 && (
              <div className="space-y-2">
                <Label htmlFor="secondaryContact">Secondary Contact (Optional)</Label>
                <Select
                  value={selectedSecondaryContactId ? selectedSecondaryContactId.toString() : ""}
                  onValueChange={(value) => setValue("secondaryContactId", value ? parseInt(value) : undefined)}
                >
                  <SelectTrigger data-testid="select-secondary-contact">
                    <SelectValue placeholder={availableSecondaryContacts.length === 0 ? "No secondary contacts available" : "Select a secondary contact..."} />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {availableSecondaryContacts.length > 0 ? (
                      availableSecondaryContacts.map((contact: any) => (
                        <SelectItem key={contact.id} value={contact.id.toString()}>
                          {contact.firstName} {contact.lastName}
                          {contact.companyName && (
                            <span className="ml-2 text-xs text-gray-500">({contact.companyName})</span>
                          )}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-secondary-contacts" disabled>No secondary contacts available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {errors.secondaryContactId && (
                  <p className="text-sm text-red-600">{errors.secondaryContactId.message}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Filter by Credentials</Label>
                <span className="text-xs text-gray-500">Optional</span>
              </div>
              <div className="border rounded-md p-2 max-h-24 overflow-y-auto bg-gray-50">
                {activeCredentialTypes.length === 0 ? (
                  <p className="text-sm text-gray-500 py-1">No credentials available</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {activeCredentialTypes.map((ct) => (
                      <label
                        key={ct.id}
                        className={`inline-flex items-center px-2 py-1 rounded-md text-sm cursor-pointer transition-colors ${
                          selectedCredentialTypeIds.includes(ct.id)
                            ? 'bg-primary text-white'
                            : 'bg-white border border-gray-200 hover:border-primary/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={selectedCredentialTypeIds.includes(ct.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCredentialTypeIds([...selectedCredentialTypeIds, ct.id]);
                            } else {
                              setSelectedCredentialTypeIds(selectedCredentialTypeIds.filter(id => id !== ct.id));
                            }
                            // Clear service type if it's no longer in the filtered list
                            if (selectedServiceType) {
                              const newSelectedIds = e.target.checked
                                ? [...selectedCredentialTypeIds, ct.id]
                                : selectedCredentialTypeIds.filter(id => id !== ct.id);
                              if (newSelectedIds.length > 0) {
                                const currentST = serviceTypes.find(st => st.id === selectedServiceType);
                                if (currentST && currentST.credentialTypeIds) {
                                  const stillValid = newSelectedIds.every(ctId => currentST.credentialTypeIds!.includes(ctId));
                                  if (!stillValid) {
                                    setValue("serviceType", "");
                                  }
                                }
                              }
                            }
                          }}
                        />
                        {ct.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedCredentialTypeIds.length > 0 && (
                <p className="text-xs text-gray-500">
                  Showing {activeServiceTypes.length} service{activeServiceTypes.length !== 1 ? 's' : ''} with selected credentials
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="serviceType">Service</Label>
              <Select
                value={selectedServiceType}
                onValueChange={(value) => setValue("serviceType", value as any)}
              >
                <SelectTrigger data-testid="select-service-type">
                  <SelectValue placeholder="Select service..." />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {activeServiceTypes.length > 0 ? (
                    activeServiceTypes.map((serviceType) => (
                      <SelectItem key={serviceType.id} value={serviceType.id}>
                        {serviceType.name}
                      </SelectItem>
                    ))
                  ) : selectedCredentialTypeIds.length > 0 ? (
                    <SelectItem value="no-matching-types" disabled>No services match selected credentials</SelectItem>
                  ) : (
                    <SelectItem value="no-service-types" disabled>Loading services...</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.serviceType && (
                <p className="text-sm text-red-600">{errors.serviceType.message}</p>
              )}
              {resolvedRate && selectedCompanyId > 0 && (
                <div className={`text-sm mt-1 px-2 py-1 rounded ${resolvedRate.isCustom ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-700'}`} data-testid="text-applicable-rate">
                  Applicable Rate: ${resolvedRate.rate}{resolvedRate.isCustom ? ' (Custom Rate)' : ' (Standard Rate)'}
                </div>
              )}
              {noRateInEffect && (
                <div className="flex items-center gap-2 text-sm mt-1 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-amber-700" data-testid="warning-no-rate">
                  <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                  No rate is in effect for this service. A rate must be added before a work order can be created.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="assignedToUserId">Assigned To</Label>
              <Select
                value={watch("assignedToUserId")}
                onValueChange={(value) => setValue("assignedToUserId", value)}
              >
                <SelectTrigger data-testid="select-assigned-to">
                  <SelectValue placeholder="Select user to assign..." />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users?.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.assignedToUserId && (
                <p className="text-sm text-red-600">{errors.assignedToUserId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter work order description..."
                {...register("description")}
                rows={3}
                data-testid="input-description"
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} data-testid="button-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || contactError !== "" || noRateInEffect} data-testid="button-submit">
              {isLoading ? "Creating..." : "Create Work Order"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
